import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import base64 from "../../data/base64.json";
import padAssets from "../../data/pad_agreement.json";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

export interface PadAgreementData {
  companyName: string;
  companyAddress: string;
  policyNo: string;
  bankNo: string;
  transitNo: string;
  bankAccountMasked: string;
  floatAmount: number;
  authorizedByName: string;
  authorizedByEmail: string | null;
  authorizedAt: string;
}

const ACCENT = "#1E85E5";
const NAVY = "#0D4378";
const NAVY_DARK = "#082E54";
const INK = "#0A0E14";
const BODY = "#374151";
const GRAY = "#6B7280";
const BORDER = "#E5E7EB";
const FILL_BLUE = "#EAF3FD";
const BORDER_BLUE = "#C7E0FA";
const FILL_GRAY = "#F9FAFB";

const fmt = (n: number) =>
  "$" + Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SUPPORT = "support@nationalhealthclaim.com";

const sectionLabel = (text: string) => ({
  table: { widths: ["*"], body: [[{ text, color: NAVY, bold: true, fontSize: 8, characterSpacing: 1 }]] },
  layout: {
    hLineWidth: (i: number, node: any) => (i === node.table.body.length ? 1 : 0),
    vLineWidth: () => 0,
    hLineColor: () => BORDER_BLUE,
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => 0,
    paddingBottom: () => 3,
  },
  margin: [0, 0, 0, 5] as number[],
});

const boxCell = (label: string, value: string, valueColor: string = INK) => ({
  stack: [
    { text: label, fontSize: 6.5, bold: true, color: GRAY, characterSpacing: 0.7, margin: [0, 0, 0, 2] },
    { text: value || "-", fontSize: 9.5, bold: true, color: valueColor },
  ],
});

const boxLayout = {
  hLineWidth: () => 0.75,
  vLineWidth: () => 0.75,
  hLineColor: () => BORDER,
  vLineColor: () => BORDER,
  paddingLeft: () => 9,
  paddingRight: () => 9,
  paddingTop: () => 6,
  paddingBottom: () => 6,
};

const para = (content: any, extra: any = {}) => ({
  text: content,
  fontSize: 8,
  color: BODY,
  lineHeight: 1.22,
  margin: [0, 0, 0, 4.5],
  ...extra,
});

const mail = { text: SUPPORT, color: ACCENT, link: "mailto:" + SUPPORT };
const paymentsCa = { text: "www.payments.ca", color: ACCENT, link: "https://www.payments.ca" };

export function buildPadAgreementDoc(data: PadAgreementData) {
  const signedDate = new Date(
    /[zZ]$|[+-]\d\d:?\d\d$/.test(data.authorizedAt) ? data.authorizedAt : data.authorizedAt + "Z"
  ).toLocaleDateString("en-CA", { timeZone: "America/Edmonton", year: "numeric", month: "long", day: "numeric" });

  const bannerRest =
    `via the National HealthClaim PA Portal on ${signedDate} by ` +
    data.authorizedByName +
    (data.authorizedByEmail ? ` (${data.authorizedByEmail})` : "") +
    `, acting as an authorized Plan Administrator on behalf of ${data.companyName}. ` +
    "Electronic authorization constitutes a valid and binding signature for the purposes of this agreement.";

  const doc = {
    pageSize: "LETTER",
    pageMargins: [40, 26, 40, 38],
    defaultStyle: { fontSize: 8, color: BODY },
    footer: () => ({
      text: [
        { text: "National HealthClaim", bold: true, color: NAVY },
        { text: "  ·  " + SUPPORT + "  ·  1 (866) 342-5908  ·  nationalhealthclaim.com", color: GRAY },
      ],
      alignment: "center",
      fontSize: 7.5,
      margin: [40, 10, 40, 0],
    }),
    content: [
      { image: base64.logo, width: 100 },
      { text: "BUSINESS PRE-AUTHORIZED DEBIT", color: ACCENT, bold: true, fontSize: 7.5, characterSpacing: 1.5, margin: [0, 8, 0, 2] },
      { text: "Pre-Authorized Debit (PAD) Agreement", color: INK, bold: true, fontSize: 18, margin: [0, 0, 0, 8] },
      {
        columnGap: 16,
        columns: [
          {
            width: "*",
            stack: [
              sectionLabel("COMPANY DETAILS"),
              {
                table: {
                  widths: ["*", "*"],
                  body: [
                    [boxCell("COMPANY NAME", data.companyName), boxCell("POLICY NUMBER", data.policyNo)],
                    [{ ...boxCell("COMPANY ADDRESS", data.companyAddress), colSpan: 2 }, {}],
                  ],
                },
                layout: boxLayout,
              },
            ],
          },
          {
            width: "*",
            stack: [
              sectionLabel("BANKING DETAILS"),
              {
                table: {
                  widths: ["*", "*"],
                  body: [
                    [boxCell("INSTITUTION NUMBER", data.bankNo), boxCell("TRANSIT NUMBER", data.transitNo)],
                    [boxCell("BANK ACCOUNT NUMBER", data.bankAccountMasked), boxCell("WEEKLY FLOAT", fmt(data.floatAmount) + "*", ACCENT)],
                    [
                      {
                        colSpan: 2,
                        text:
                          "*This is the minimum balance we keep on hand in your account so employee claims are paid promptly. " +
                          "Your PAD debits are calculated weekly to maintain this amount as claims activity occurs.",
                        fontSize: 6.8,
                        italics: true,
                        color: GRAY,
                        lineHeight: 1.25,
                      },
                      {},
                    ],
                  ],
                },
                layout: boxLayout,
              },
            ],
          },
        ],
      },
      {
        margin: [0, 9, 0, 9],
        table: {
          widths: ["*"],
          body: [
            [
              {
                text: [
                  "The Company/Plan Administrator is solely responsible for ensuring that accurate banking information is provided. ",
                  { text: "National HealthClaim is not liable", bold: true },
                  " for payments processed based on incorrect or incomplete banking information supplied by the Company/Plan Administrator.",
                ],
                color: NAVY,
                fontSize: 8,
                lineHeight: 1.25,
              },
            ],
          ],
        },
        layout: {
          fillColor: () => FILL_BLUE,
          hLineWidth: () => 0,
          vLineWidth: (i: number) => (i === 0 ? 3 : 0),
          vLineColor: () => ACCENT,
          paddingLeft: () => 10,
          paddingRight: () => 10,
          paddingTop: () => 8,
          paddingBottom: () => 8,
        },
      },
      sectionLabel("PRE-AUTHORIZED DEBIT AGREEMENT"),
      para(
        "This authorization constitutes a Business Pre-Authorized Debit (PAD) Agreement under Payments Canada Rule H1. " +
          "I authorize National HealthClaim and its financial institution to debit the bank account identified in this agreement " +
          "for recurring and/or one-time payments required to fund my National HealthClaim account and pay charges arising under my account."
      ),
      para(
        "I acknowledge that debit amounts are variable and may differ from one withdrawal to the next based on the minimum account " +
          "balance required to maintain company-wide assigned employee limits (or weekly float amount) and ongoing claims activity; " +
          "debits may be processed weekly, as required, and will generally occur on Thursdays."
      ),
      para(
        "I waive the standard ten (10) calendar day advance notice requirement for variable-amount PADs and agree to receive " +
          "notification of the debit amount by email on the day the withdrawal is processed.",
        { bold: true, color: INK }
      ),
      para(
        "Funds will be credited to my National HealthClaim account approximately five (5) business days after the debit, provided " +
          "no payment failure or rejection notice is received from my financial institution. If a payment is returned or rejected, " +
          "National HealthClaim may suspend PAD funding until the issue is resolved."
      ),
      para([
        "This authorization remains in effect until National HealthClaim receives written notice of its amendment or termination. " +
          "I may cancel this PAD Agreement at any time by providing written notice to ",
        mail,
        ", and National HealthClaim will process my cancellation within 30 calendar days of receipt.",
      ]),
      para("To stop a scheduled debit, notice must be received at least five (5) business days before the debit date."),
      para(
        [
          "I have certain recourse rights if any debit does not comply with this PAD Agreement, including the right to reimbursement " +
            "for any debit that is not authorized or is not consistent with this agreement. For more information on my recourse rights, " +
            "I may contact my financial institution or visit ",
          paymentsCa,
          ". For questions regarding this PAD Agreement, I will contact National HealthClaim at ",
          mail,
          ".",
        ],
        { margin: [0, 0, 0, 8] }
      ),
      sectionLabel("ACCEPTANCE OF AGREEMENT"),
      {
        margin: [0, 2, 0, 9],
        columnGap: 16,
        columns: [
          {
            width: "*",
            table: {
              widths: ["*"],
              body: [
                [
                  {
                    stack: [
                      {
                        columns: [
                          { text: "NATIONAL HEALTHCLAIM", color: NAVY, bold: true, fontSize: 7, characterSpacing: 0.7 },
                          { text: signedDate, color: GRAY, fontSize: 7.5, alignment: "right", width: "auto" },
                        ],
                      },
                      {
                        text: [
                          { text: "Shawna Zawada", bold: true, fontSize: 10, color: INK },
                          { text: "  |  President", fontSize: 9, color: GRAY },
                        ],
                        margin: [0, 6, 0, 4],
                      },
                      { text: "SIGNATURE", fontSize: 6.5, bold: true, color: GRAY, characterSpacing: 0.7 },
                      { image: padAssets.signature, width: 78, margin: [24, 0, 0, 0] },
                      { canvas: [{ type: "line", x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 0.75, lineColor: "#9AA2AE" }], margin: [0, -8, 0, 2] },
                    ],
                  },
                ],
              ],
            },
            layout: {
              fillColor: () => FILL_BLUE,
              hLineWidth: () => 0.75,
              vLineWidth: () => 0.75,
              hLineColor: () => BORDER_BLUE,
              vLineColor: () => BORDER_BLUE,
              paddingLeft: () => 10,
              paddingRight: () => 10,
              paddingTop: () => 8,
              paddingBottom: () => 8,
            },
          },
          {
            width: "*",
            table: {
              widths: ["*"],
              body: [
                [
                  {
                    stack: [
                      {
                        columns: [
                          { text: "SIGNING PLAN ADMINISTRATOR", color: NAVY, bold: true, fontSize: 7, characterSpacing: 0.7 },
                          { text: signedDate, color: GRAY, fontSize: 7.5, alignment: "right", width: "auto" },
                        ],
                      },
                      { text: "ELECTRONICALLY SIGNED BY", fontSize: 6.5, bold: true, color: GRAY, characterSpacing: 0.7, margin: [0, 10, 0, 3] },
                      { text: data.authorizedByName, bold: true, fontSize: 10.5, color: INK, margin: [0, 0, 0, 6] },
                      { canvas: [{ type: "line", x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 0.75, lineColor: "#9AA2AE" }], margin: [0, 0, 0, 2] },
                    ],
                  },
                ],
              ],
            },
            layout: {
              fillColor: () => FILL_GRAY,
              hLineWidth: () => 0.75,
              vLineWidth: () => 0.75,
              hLineColor: () => BORDER,
              vLineColor: () => BORDER,
              paddingLeft: () => 10,
              paddingRight: () => 10,
              paddingTop: () => 8,
              paddingBottom: () => 8,
            },
          },
        ],
      },
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                text: [{ text: "This agreement was authorized electronically ", bold: true }, bannerRest],
                color: "#FFFFFF",
                fontSize: 7.5,
                lineHeight: 1.28,
              },
            ],
          ],
        },
        layout: {
          fillColor: () => NAVY_DARK,
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 10,
          paddingRight: () => 10,
          paddingTop: () => 7,
          paddingBottom: () => 7,
        },
      },
      {
        text: [
          "This document is a record of your PAD authorization. Please retain it for your records. " +
            "For recourse rights information, contact your financial institution or visit ",
          paymentsCa,
          ".",
        ],
        fontSize: 6.5,
        color: GRAY,
        alignment: "center",
        margin: [0, 7, 0, 0],
      },
    ],
  };

  return doc;
}

export function downloadPadAgreement(data: PadAgreementData) {
  pdfMake.createPdf(buildPadAgreementDoc(data) as any).download("PAD_Agreement_" + (data.policyNo || "policy") + ".pdf");
}
